import { DecisionStore } from 'nomst/Stores/Decision/decision.store';
import { History } from 'history';
import { NavigationStore } from 'nomst/Stores/Navigation/navigation.store';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { User } from 'nomst/Stores/User/user';
import { EligibilityProrationOverrideStore } from 'nomst/Stores/Decision/eligibilityProrationOverrideStore';
import { SummaryDashboardStore } from 'nomst/Stores/SummaryDashboard/summaryDashboard.store';
import { action, computed, observable, makeObservable } from 'mobx';
import { configurationsStore, cycleRules } from 'utils/MobxDataManager/singletons';
import openIdAuthentication from 'authentication/openIdAuthentication';
import { notificationStore } from 'utils/MobxDataManager/singletons';
import { Alert } from 'nomst/Stores/Utility/alert';
import { Dropdown } from '@payscale/design';
import { PageTabs, Roles } from 'typings/enums';

export class TeamStore {
    @observable appConfiguration: IAppConfiguration;
    alert: Alert;
    @observable settingsDropdown: typeof Dropdown;
    @observable currentPageTab: PageTabs;
    private _decisionStore: DecisionStore;
    private _navigationStore: NavigationStore;
    private _user: User;
    private _eligibilityOverrideStore: EligibilityProrationOverrideStore;
    private _summaryDashboardStore: SummaryDashboardStore;

    constructor(
        history: History,
        user: User) {
        makeObservable(this);
        this._user = user;
        this._decisionStore = new DecisionStore(configurationsStore.currentConvertedConfiguration);
        this._navigationStore = new NavigationStore(this, history, this.setPageTab);
        this._eligibilityOverrideStore =
                new EligibilityProrationOverrideStore(configurationsStore.currentConvertedConfiguration);
        this._summaryDashboardStore = new SummaryDashboardStore(configurationsStore._masterAccountId);
        this.appConfiguration = {
            employeeServiceUrl: '',
            conversationServiceUrl: '',
            jobCollabBaseUrl: ''
        } as IAppConfiguration;
        this.alert = new Alert();
    }

    get masterAccountId(): string {
        return configurationsStore.masterAccountId;
    }

    get decisionStore(): DecisionStore {
        return this._decisionStore;
    }

    get navigationStore(): NavigationStore {
        return this._navigationStore;
    }

    @computed
    get configuration(): Configuration {
        return configurationsStore.currentConvertedConfiguration;
    }

    get user(): User {
        return this._user;
    }

    get eligibilityProrationOverrideStore(): EligibilityProrationOverrideStore {
        return this._eligibilityOverrideStore;
    }

    get summaryDashboardStore(): SummaryDashboardStore {
        return this._summaryDashboardStore;
    }

    get rootClasses(): string {
        if (notificationStore.visible) {
            return 'overlay-active-notification';
        }

        if (this.currentPageTab !== PageTabs.Teams) {
            return '';
        }

        return '';
    }

    get jobCollabEmployeesUrl(): string {
        return `${this.appConfiguration.jobCollabBaseUrl}/employees`;
    }

    get jobCollabJobsUrl(): string {
        return `${this.appConfiguration.jobCollabBaseUrl}/jobs`;
    }

    hideHeaderForPdf(): boolean {
        return this._navigationStore.isInPdfRoute();
    }

    showMenuForRoles(...roles: string[]): boolean {
        const result = roles.filter(item => this.user._rolesMap.has(item as Roles));
        return result?.length > 0;
    }

    @action
    bindSettingsDropdown(element: typeof Dropdown): void {
        this.settingsDropdown = element;
    }

    async logout(): Promise<void> {
        await openIdAuthentication.signOut();

        // Delete the master account cookie just in case by setting it to a day ago.
        const date = new Date();
        date.setTime(date.getTime() + (-1 * 24 * 60 * 60 * 1000));
        const utcDate = date.toUTCString();
        document.cookie = 'masterAccountIdCookieType=; expires=' + utcDate + '; path=/';

        // explicitly delete the mx front end cookie when logging out of team pay.
        // should work in prod and localhost as they are same domain
        // in prestaging - expected to fail.
        // tslint:disable:no-element-overwrite This is actually how setting cookies works. Javascript man.....
        document.cookie = '_mx_maid=; expires=' + utcDate + '; path=/';
    }

    @action.bound
    setPageTab(tab: PageTabs): void {
        this.currentPageTab = tab;
    }

    @action
    goToTeams(): void {
        this.currentPageTab = PageTabs.Teams;
        if (!this.user.employeeSourceId) {
            //// TODO - Add redirect to a page that user can select a manager
            return;
        }

        this.navigationStore.goToTeams(
            this.user.employeeSourceId, configurationsStore.currentConvertedConfiguration.cycleId);
    }

    @action
    goToJobs(): void {
        this.currentPageTab = PageTabs.Jobs;
        this.navigationStore.goToJobs();
    }

    @action
    goToJobDetailsView(jobCode: string, jobId: string | number): void {
        this.currentPageTab = PageTabs.Jobs;
        this.navigationStore.goToJobDetailsView(jobCode, jobId);
    }

    @action
    goToEmployees(): void {
        this.currentPageTab = PageTabs.Employees;
        this.navigationStore.goToEmployees();
    }

    @action
    goToEmployeesDetailsView(employeeSourceId: string): void {
        this.currentPageTab = PageTabs.Employees;
        this.navigationStore.goToEmployeesDetailsView(employeeSourceId);
    }

    @action
    goToOverview(): void {
        this.currentPageTab = PageTabs.Overview;
        this.navigationStore.goToOverview(configurationsStore.currentConvertedConfiguration.cycleId);
    }

    @action
    goToGlobalConfiguration(): void {
        this.settingsDropdown.toggle();
        this.currentPageTab = PageTabs.Settings;
        this.navigationStore.goToGlobalConfiguration();
    }

    @action
    goToCycleConfiguration(): void {
        this.settingsDropdown.toggle();
        this.currentPageTab = PageTabs.Settings;
        const cycleId = configurationsStore.currentConvertedConfiguration.cycleId;
        this.navigationStore.goToCycleConfiguration(
            cycleId, cycleRules.isCycleClosedForId(cycleId));
    }

    @action
    goToLandingPage(): void {
        if (this.user.hasHrRole || this.user.isHrOrSupport || this.user.isHrbp || !this.user.isFirstLevelManager) {
            this.navigationStore.goToOverview(configurationsStore.currentConvertedConfiguration.cycleId);
            return;
        }
        this.navigationStore.goToTeams(this.user.employeeSourceId ?? '', configurationsStore.currentConvertedConfiguration.cycleId);
    }

    @action
    redirectToLandingPage(): void {
        if (this.navigationStore.noRoute()) {
            return;
        }

        this.goToLandingPage();
    }

    @action
    updateMobxFromMobxStateTree(
        user: User,
        setPageTab: (tab: PageTabs) => void): void {
        this._user = user;
        this._decisionStore = new DecisionStore(configurationsStore.currentConvertedConfiguration);
        this._navigationStore.updateNavigationStore(
            this._decisionStore, configurationsStore.currentConvertedConfiguration, setPageTab);
        this._eligibilityOverrideStore = new EligibilityProrationOverrideStore(this.configuration);
        this._summaryDashboardStore = new SummaryDashboardStore(configurationsStore._masterAccountId);
    }

    @action
    updateConfiguration(): void {
        const setPageTab = this._navigationStore.decisionNavigationStore.setPageTab;
        const config = configurationsStore.currentConvertedConfiguration;
        this._decisionStore = new DecisionStore(config);
        this._navigationStore.updateNavigationStore(this._decisionStore, config, setPageTab);
        this._eligibilityOverrideStore = new EligibilityProrationOverrideStore(config);
    }

    @action
    setAppConfiguration(appConfig: IAppConfiguration): void {
        this.appConfiguration = appConfig;
    }

    @action
    setCurrentUserTest(user: User): void {
        this._user = user;
    }
}